import {type ActionTypes} from './actions';

export enum FLEX_SEARCH_VERSION {
  SIX,
  SEVEN,
}

export type UseFuzzySearchProps<Value, FoundValue = Value> = {
  /** List of items to search */
  items: Iterable<Value>;
  /**
   * Function that is called on each item to determine which field(s) should be
   * searched. Eg if we want to search the text coming from `item.name`:
   *
   * ```js
   * const getSearchableText = item => item.name;
   * ```
   */
  getSearchableText: (value: Value) => string;

  /** Custom matching returns item indices without building a FlexSearch index. */
  search?: (query: string, limit: number, suggest: boolean) => number[];

  /**
   * Allows you to control the query directly and will not use internal state.
   * This prop should be used in combination with `onQueryChange`
   *
   * > See also: `onQueryChange`
   */
  query?: string;

  /**
   * If controlled, this handler will fire with the new query as its first
   * argument. This prop should be used in combination with `query`
   *
   * > See also: `query`
   */
  onQueryChange?: (query: string) => void;

  /** Handler that is fired after a search result is selected */
  onSelectChange?: (value: FoundValue) => void;

  /** Handler that is fired after a search result is preselected  */
  onPreselectChange?: (value: FoundValue) => void;

  /** Handler that is fired after there is no search result being preselected */
  onUnpreselected?: () => void;

  /** Max number of search results to show. */
  limit?: number;

  /**
   * If query string is empty, should we show all results? Default is true
   */
  showAll?: boolean;

  /**
   * An escape hatch that provide the ability to customize a given list of
   * results. An example would be to always persist a static result item as
   * the first item.
   */
  reduceItems?: ReduceItems<Value, FoundValue>;

  /*
   * An escape hatch used to determine the next index when cycling through results
   * using the ArrowUp/ArrowDown keys. The default shallow checks by reference, but there
   * are cases where the original search list item references may change and therefore needs
   * another way to find the next index. This escape hatch allows just that.
   */
  findNextIndex?: findNextIndexType<FoundValue>;

  /**
   * If true, the search index will return suggested words while matching
   */
  suggest?: boolean;

  /**
   * Defaults to false.
   *
   * If true, defer computing of the search index until it is absolutely needed.
   * Useful if fuzzy-search is not the primary purpose of the UI that you're
   * using this within.
   *
   * In practice, this means the search index won't be created until a search
   * query is provided in some capacity (typically when you start typing into a
   * search input).
   *
   * WARNING: For larger sets of items, this can mean that the first query can
   * have a substantial delay before results start to appear, since the search
   * index is generated in the main thread.
   *
   * When passing `lazy: true`, you can manually "warm up" the search index by
   * calling `useFuzzySearch(...).handlers.warmUpSearchIndex()`, but this is not
   * a strict requirement as the index will be created once it's needed,
   * regardless.
   */
  lazy?: boolean;

  /**
   * If true, pressing "Tab" on a search result will also select the result (in
   * addition to pressing Enter). Does not select if shift is held down.
   */
  tabToSelect?: boolean;

  version?: FLEX_SEARCH_VERSION;
};

type Handlers = {
  onChange: (event: React.ChangeEvent<EventTarget>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent<EventTarget>) => void;

  /** Clears the search query */
  clear: () => void;

  /**
   * Generates the search index (if it hasn't already been generated).
   *
   * Only useful if you called `useFuzzySearch` with `lazy: true`, otherwise
   * this has no effect, as the search index should have already been created by
   * the time the hook returns.
   */
  warmUpSearchIndex: () => void;
};

export type UseFuzzySearchReturn<FoundValue> = [
  Matches<FoundValue>,
  State<FoundValue>,
  Handlers,
];

export interface ItemActions {
  /**
   * Selects the match. Note: should have an `onSelectChange` handler passed
   * into the hook be useful. See also: `onSelectChange`.
   */
  select(): void;

  /**
   * Preselects the match. Note: should have an `onPreselectChange` handler
   * passed into the hook to be useful. See also: `onPreselectChange`.
   */
  preselect(): void;

  /** Unpreselects the match */
  unpreselect(): void;
}

export type Matches<Value> = Array<{
  /** The item that was matched via searching */
  match: Value;

  /**
   * The actions that a user can perform on the item, such as clicking or
   * hovering the item in a search list.
   * */
  actions: ItemActions;
}>;

export type State<Value> = {
  /** The internal input value state */
  query: string;

  /** Should the input focus or not? */
  focus: boolean;

  /**
   * The currently selected item. Used so that when changed, an effect
   * is fired with an optional onChange handler of the new selected item
   */
  selected: null | Value;

  /**
   * The currently preselected item. Used so that when changed, an effect
   * is fired with an optional onChange handler of the new selected item
   */
  preselected: null | Value;
};

export type Action<Value> =
  | {
      type: ActionTypes['FOCUSED'];
    }
  | {
      type: ActionTypes['BLURRED'];
    }
  | {
      type: ActionTypes['PATTERN_CHANGED'];
      payload: string;
    }
  | {
      type: ActionTypes['MATCH_SELECTED'];
      payload: Value;
    }
  | {
      type: ActionTypes['MATCH_PRESELECTED'];
      payload: Value | null;
    }
  | {
      type: ActionTypes['KEY_PRESSED'];
      payload: {
        key: string;
        results: Matches<Value>;
        metaKey: boolean;
        shiftKey: boolean;
        findNextIndex: findNextIndexType<Value>;
        tabToSelect: boolean;
      };
    };

type ReduceItems<Value, FoundValue> = (
  foundIndexes: Array<number>,
  items: Array<Value>,
  mapItemAction: (value: FoundValue) => ItemActions
) => Matches<FoundValue>;

type findNextIndexType<FoundValue> = (
  results: Matches<FoundValue>,
  preselected: FoundValue | null,
  key: string
) => number;
